import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Grid from '@mui/material/Grid'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'

import { NumberInput, SelectField, SwitchField, PDFViewer } from '../shared'
import RoofPitch from '../../RoofPitch'
import CompassControl from '../../CompassControl'
import ErrorDialog from '../../ErrorDialog'
import { Alerts1, Alerts2 } from '../../Alerts'
import { GetPanels, GetInstallationLocations, APIGetSolarOptimisers, getSolarSavigsFromServer, APICreateDataSheets } from '../../Api'
import PanelLayoutSets from '../PanelLayoutSets'

// Redux imports
import {
    selectPanelArrays,
    setPanelArrays,
    selectBattery
} from '../../store/slices/systemSlice'
import {
    selectInstallationLocations,
    setInstallationLocations,
    setPanels as setCatalogPanels,
    selectPanels as selectCatalogPanels
} from '../../store/slices/catalogSlice'
import {
    selectElecConsumption,
    selectElecRate,
    selectElectricEstimated,
    selectGasConsumption,
    selectGasRate,
    selectGasEstimated,
    selectSelectedTariff,
    selectSelfConsumption
} from '../../store/slices/energySlice'
import {
    selectPostcode,
    selectCustomerName,
    selectFullAddress,
    selectFlgId
} from '../../store/slices/customerSlice'

// Styles
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2
    },
    toolbar: {
        bgcolor: '#ffffff',
        mb: 2
    },
    tabs: {
        bgcolor: '#6fcae7',
        color: '#fff'
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
    },
    sectionTitle: {
        fontWeight: 'bold',
        fontSize: 14,
        borderBottom: '1px solid #ddd',
        pb: 0.5,
        mb: 1
    },
    formRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 2
    },
    buttonRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mt: 1
    },
    visualControls: {
        mt: 2
    },
    controlHeader: {
        fontSize: 10,
        textAlign: 'center'
    }
}

// Default panel array template
const createDefaultArray = (index, basePanel) => ({
    key: index,
    arrayNumber: index + 1,
    numPanels: 8,
    panel_id: basePanel?.panel_id || 9,
    panelType: basePanel ? `${basePanel.description} - ${basePanel.type}` : 'Default Panel',
    watts: basePanel?.watts || 400,
    panelModel: basePanel?.description || 'Default',
    roofPitch: 35,
    orientation: 0,
    shading: 0.9,
    description: `Array ${index + 1} Description`,
    birdprotection: false,
    unit_cost: basePanel?.unit_cost || 0,
    customer_price: basePanel?.customer_price || 0,
    unit_qty: basePanel?.unit_qty || 1,
    installation_location: {},
    optimiser: {},
    qty_optimisers: 0,
    calcData: {},
    layoutSets: [],  // For rails/hooks calculation
    width_mm: basePanel?.width_mm || 1134,
    height_mm: basePanel?.height_mm || 1722
})

const PanelsTab = () => {
    const dispatch = useDispatch()

    // Redux state - System
    const panelArrays = useSelector(selectPanelArrays) || []
    const installationLocations = useSelector(selectInstallationLocations) || []
    const catalogPanels = useSelector(selectCatalogPanels) || []
    const battery = useSelector(selectBattery)

    // Redux state - Energy
    const elecConsumption = useSelector(selectElecConsumption)
    const elecRate = useSelector(selectElecRate)
    const electricEstimated = useSelector(selectElectricEstimated)
    const gasConsumption = useSelector(selectGasConsumption)
    const gasRate = useSelector(selectGasRate)
    const gasEstimated = useSelector(selectGasEstimated)
    const selectedTariff = useSelector(selectSelectedTariff)
    const selfConsumption = useSelector(selectSelfConsumption)

    // Redux state - Customer
    const postcode = useSelector(selectPostcode)
    const customerName = useSelector(selectCustomerName)
    const fullAddress = useSelector(selectFullAddress)
    const flgId = useSelector(selectFlgId)

    // Local state
    const [currentArray, setCurrentArray] = useState(0)
    const [panels, setPanels] = useState([])
    const [optimisers, setOptimisers] = useState([])
    const [calculating, setCalculating] = useState(false)
    const [dontupdateAll, setDontupdateAll] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleteArrayNo, setDeleteArrayNo] = useState(-1)
    const [showArrayData, setShowArrayData] = useState(false)
    const [error, setError] = useState({ open: false, title: '', text: '' })
    const [dataLoaded, setDataLoaded] = useState(false)
    const [pdfViewer, setPdfViewer] = useState({ open: false, filename: '', title: '' })

    // Load catalog data on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                const [panelsData, locationsData, optimisersData] = await Promise.all([
                    GetPanels(),
                    GetInstallationLocations(),
                    APIGetSolarOptimisers()
                ])
                setPanels(panelsData || [])
                setOptimisers(optimisersData || [])

                if (panelsData) {
                    dispatch(setCatalogPanels(panelsData))
                }
                if (locationsData) {
                    dispatch(setInstallationLocations(locationsData))
                }

                // Initialize with one array if empty
                if (panelArrays.length === 0 && panelsData?.length > 0) {
                    const defaultPanel = panelsData.find(p => p.panel_id === 9) || panelsData[8]
                    dispatch(setPanelArrays([createDefaultArray(0, defaultPanel)]))
                }
                setDataLoaded(true)
            } catch (err) {
                console.error('Failed to load panel data:', err)
                setDataLoaded(true) // Still set to true so UI shows
            }
        }
        loadData()
    }, [])

    // Current array data
    const curArray = panelArrays[currentArray] || {}
    const hasItems = panelArrays.length > 0

    // Build options for dropdowns - filter out any null/undefined items
    const numArrayOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => ({ value: n, label: String(n) }))

    const panelOptions = (panels || []).filter(p => p && p.panel_id).map(p => ({
        value: p.panel_id,
        label: `${p.description || 'Unknown'}${p.outofstock ? ' (Out of Stock)' : ''}`
    }))

    const locationOptions = (installationLocations || []).filter(loc => loc && loc.installation_id).map(loc => ({
        value: parseInt(loc.installation_id),
        label: loc.description || 'Unknown'
    }))

    const optimiserOptions = (optimisers || []).filter(opt => opt && opt.uid !== undefined).map(opt => ({
        value: opt.uid,
        label: opt.description || 'Unknown'
    }))

    // Handlers
    const handleTabChange = (event, value) => {
        setCurrentArray(value)
    }

    const handleNumArraysChange = (value) => {
        if (value > panelArrays.length) {
            const basePanel = panels.find(p => p.panel_id === 9) || panels[8]
            const newArrays = [...panelArrays]
            for (let i = panelArrays.length; i < value; i++) {
                newArrays.push(createDefaultArray(i, basePanel))
            }
            dispatch(setPanelArrays(newArrays))
        }
    }

    const handleDeleteArray = (arrayNo) => {
        setDeleteArrayNo(arrayNo)
        setConfirmDelete(true)
    }

    const handleConfirmDelete = (confirmed) => {
        if (confirmed && deleteArrayNo >= 0) {
            const newArrays = panelArrays
                .filter(item => item.key !== deleteArrayNo)
                .map((item, idx) => ({ ...item, key: idx, arrayNumber: idx + 1 }))
            dispatch(setPanelArrays(newArrays))
            setCurrentArray(0)
        }
        setConfirmDelete(false)
        setDeleteArrayNo(-1)
    }

    const updateCurrentArray = (updates) => {
        const newArrays = panelArrays.map((arr, idx) =>
            idx === currentArray ? { ...arr, ...updates } : arr
        )
        dispatch(setPanelArrays(newArrays))
    }

    const handlePanelTypeChange = (panelId) => {
        const selPanel = panels.find(p => p.panel_id === panelId)
        if (!selPanel) return

        const updates = {
            panel_id: selPanel.panel_id,
            panelType: `${selPanel.description} - ${selPanel.type}`,
            watts: selPanel.watts,
            panelModel: selPanel.description,
            unit_cost: selPanel.unit_cost,
            customer_price: selPanel.customer_price,
            unit_qty: selPanel.unit_qty,
            width_mm: selPanel.width_mm || 1134,
            height_mm: selPanel.height_mm || 1722,
            datasheet_filename: selPanel.datasheet_filename || null
        }

        if (dontupdateAll) {
            updateCurrentArray(updates)
        } else {
            const newArrays = panelArrays.map(arr => ({ ...arr, ...updates }))
            dispatch(setPanelArrays(newArrays))
        }
    }

    const handleLayoutSetsChange = (newLayoutSets) => {
        updateCurrentArray({ layoutSets: newLayoutSets })
    }

    const handleViewDatasheet = () => {
        const selPanel = panels.find(p => p.panel_id === curArray.panel_id)
        if (selPanel?.datasheet_filename) {
            setPdfViewer({
                open: true,
                filename: selPanel.datasheet_filename,
                title: `${selPanel.description} - Datasheet`
            })
        }
    }

    const currentPanelHasDatasheet = () => {
        const selPanel = panels.find(p => p.panel_id === curArray.panel_id)
        return Boolean(selPanel?.datasheet_filename)
    }

    const handleInstallationLocationChange = (locationId) => {
        const location = installationLocations.find(l => parseInt(l.installation_id) === locationId)
        if (location) {
            updateCurrentArray({ installation_location: { ...location } })
        }
    }

    const handleOptimiserChange = (uid) => {
        const selOptimiser = optimisers.find(o => o.uid === uid)
        if (!selOptimiser) return

        if (selOptimiser.type === 'SE') {
            const newArrays = panelArrays.map(arr => ({
                ...arr,
                optimiser: { ...selOptimiser },
                qty_optimisers: parseInt(arr.numPanels)
            }))
            dispatch(setPanelArrays(newArrays))
        } else {
            const newArrays = panelArrays.map((arr, idx) => {
                if (idx === currentArray) {
                    return { ...arr, optimiser: { ...selOptimiser }, qty_optimisers: parseInt(arr.numPanels) }
                }
                if (arr.optimiser?.type === 'SE') {
                    return { ...arr, optimiser: optimisers[0] || {} }
                }
                return arr
            })
            dispatch(setPanelArrays(newArrays))
        }
    }

    // Visual control handlers
    const handleRoofPitchSlider = (e) => updateCurrentArray({ roofPitch: e.roofpitch })
    const handleCompassSlider = (e) => updateCurrentArray({ orientation: e.degrees })

    const handleCalculate = async () => {
        setCalculating(true)
        try {
            // Build base payload from Redux state
            const payload = {
                postcode: postcode ? postcode.toUpperCase() : '',
                selfconsumption: selfConsumption,
                elecrate: elecRate,
                electric_estimated: electricEstimated,
                elecconsumption: elecConsumption,
                gasconsumption: gasConsumption,
                gasrate: gasRate,
                gas_estimated: gasEstimated,
                battery: battery,
                selected_tariff: selectedTariff,
                total_pv_output: 0,
                name: customerName,
                full_address: fullAddress,
            }

            // Step 1: Loop through arrays and get energy for each panel array using CalcSolarArray
            let totalPower = 0
            const updatedArrays = JSON.parse(JSON.stringify(panelArrays)) // Deep copy
            let lastResult = null

            for (let i = 0; i < updatedArrays.length; i++) {
                payload.panelArrays = updatedArrays[i]
                const result = await getSolarSavigsFromServer(payload, 'CalcSolarArray')

                if (result.status) {
                    totalPower += result.payload.panel_production_energy
                    updatedArrays[i].irradiance = result.payload.irradiance
                    updatedArrays[i].zone = result.payload.zone
                    lastResult = result
                } else {
                    setError({
                        open: true,
                        title: 'Error Getting Panel Details',
                        text: result.error || 'Unknown error occurred'
                    })
                    setCalculating(false)
                    return
                }
            }

            // Step 2: If no electricity consumption, just save basic data
            if (!elecConsumption || elecConsumption === 0) {
                lastResult.payload.panel_useable_energy = 0
                lastResult.payload.panel_and_battery_useable_energy = 0
                lastResult.payload.battery_uplift = 0
                lastResult.payload.elecconsumption = 0
                updatedArrays[currentArray].calcData = lastResult.payload
                dispatch(setPanelArrays(updatedArrays))
                setShowArrayData(true)
                setCalculating(false)
                return
            }

            // Step 3: Get self-consumption data with SolarArraysReport
            payload.total_pv_output = totalPower
            payload.panelArrays = updatedArrays
            const sc = await getSolarSavigsFromServer(payload, 'SolarArraysReport')
            console.log('SolarArraysReport returned:', sc)

            if (sc.status) {
                // Step 4: Create PDF from the report
                if (flgId) {
                    const html_page = flgId + "_epvs.html"
                    const pdf_page = flgId + "_epvs.pdf"
                    const html = "<html><head><style> body{font-family: Arial,Helvetica; font-size: 12pt;margin: 10px 10px 10px 10px} </style></head><body>" + sc.report + "</body></html>"
                    await APICreateDataSheets(flgId, html, html_page, pdf_page, "portrait")
                }

                // Step 5: Update calcData with self-consumption results
                lastResult.payload.panel_useable_energy = sc.solar
                lastResult.payload.panel_and_battery_useable_energy = sc.solar_battery
                lastResult.payload.battery_uplift = sc.battery_uplift
                lastResult.payload.elecconsumption = elecConsumption
                lastResult.payload.report = sc.report
                lastResult.payload.mgd003_report = sc.mgd003_report
                updatedArrays[currentArray].calcData = lastResult.payload

                dispatch(setPanelArrays(updatedArrays))
                setShowArrayData(true)
            } else {
                setError({
                    open: true,
                    title: 'Error Getting Self Consumption Data',
                    text: sc.error || 'Unknown error occurred'
                })
            }
        } catch (err) {
            setError({ open: true, title: 'Calculation Error', text: err.message })
        } finally {
            setCalculating(false)
        }
    }

    // Show loading state until data is loaded
    if (!dataLoaded) {
        return (
            <Box sx={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={styles.container}>
            <Backdrop open={calculating} sx={{ zIndex: 1000 }}>
                <CircularProgress color="inherit" size={100} />
            </Backdrop>

            <Alerts1 open={confirmDelete} onClose={handleConfirmDelete} state={{ deleteArrayNo }} />
            <Alerts2 open={showArrayData} state={{ panelArrays, currentArray }} onClose={() => setShowArrayData(false)} />
            <ErrorDialog title={error.title} text={error.text} open={error.open} onClose={() => setError({ open: false, title: '', text: '' })} />
            <PDFViewer
                open={pdfViewer.open}
                onClose={() => setPdfViewer({ open: false, filename: '', title: '' })}
                filename={pdfViewer.filename}
                title={pdfViewer.title}
                type="datasheet"
            />

            {/* Array Selection Toolbar */}
            <AppBar position="static" sx={styles.toolbar} elevation={0}>
                <Toolbar variant="dense">
                    <SelectField
                        label="# Arrays"
                        value={panelArrays.length}
                        onChange={handleNumArraysChange}
                        options={numArrayOptions}
                        sx={{ minWidth: 80, mr: 2 }}
                    />
                    {hasItems && (
                        <Tabs value={currentArray} onChange={handleTabChange} sx={styles.tabs} textColor="inherit">
                            {panelArrays.map(item => (
                                <Tab key={item.key} label={item.arrayNumber} sx={{ minWidth: 40 }} />
                            ))}
                        </Tabs>
                    )}
                </Toolbar>
            </AppBar>

            {/* Array Configuration */}
            {hasItems && curArray && (
                <>
                    <Box sx={styles.section}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={styles.sectionTitle}>
                                Array {curArray.arrayNumber} Configuration
                            </Typography>
                            <IconButton color="secondary" onClick={() => handleDeleteArray(curArray.key)} size="small">
                                <DeleteForeverOutlinedIcon />
                            </IconButton>
                        </Box>

                        <TextField
                            label="Description"
                            value={curArray.description || ''}
                            onChange={(e) => updateCurrentArray({ description: e.target.value })}
                            size="small"
                            fullWidth
                        />

                        <Box sx={styles.formRow}>
                            <SwitchField
                                label="Don't update all"
                                checked={dontupdateAll}
                                onChange={setDontupdateAll}
                            />
                            <NumberInput
                                label="# Panels"
                                value={curArray.numPanels || 0}
                                onChange={(val) => updateCurrentArray({ numPanels: val })}
                                decimalScale={0}
                                sx={{ width: 100 }}
                            />
                            <SelectField
                                label="Panel Type"
                                value={curArray.panel_id || ''}
                                onChange={handlePanelTypeChange}
                                options={panelOptions}
                                sx={{ minWidth: 200 }}
                            />
                            {currentPanelHasDatasheet() && (
                                <IconButton
                                    color="primary"
                                    onClick={handleViewDatasheet}
                                    title="View Panel Datasheet"
                                    size="small"
                                >
                                    <PictureAsPdfIcon />
                                </IconButton>
                            )}
                        </Box>

                        <Box sx={styles.formRow}>
                            <NumberInput
                                label="Roof Pitch"
                                value={curArray.roofPitch || 35}
                                onChange={(val) => updateCurrentArray({ roofPitch: Math.max(0, Math.min(90, val)) })}
                                decimalScale={0}
                                suffix="°"
                                sx={{ width: 100 }}
                            />
                            <NumberInput
                                label="Orientation"
                                value={curArray.orientation || 0}
                                onChange={(val) => updateCurrentArray({ orientation: Math.max(0, Math.min(175, val)) })}
                                decimalScale={0}
                                suffix="°"
                                sx={{ width: 100 }}
                            />
                            <NumberInput
                                label="Shading Factor"
                                value={curArray.shading || 0.9}
                                onChange={(val) => updateCurrentArray({ shading: Math.max(0, Math.min(1, val)) })}
                                decimalScale={2}
                                sx={{ width: 100 }}
                            />
                        </Box>
                    </Box>

                    {/* Options Section */}
                    <Box sx={styles.section}>
                        <Typography sx={styles.sectionTitle}>Options</Typography>

                        <Box sx={styles.formRow}>
                            <SwitchField
                                label="Bird Protection"
                                checked={curArray.birdprotection || false}
                                onChange={(val) => updateCurrentArray({ birdprotection: val })}
                            />
                            <SelectField
                                label="Installation Location"
                                value={parseInt(curArray.installation_location?.installation_id) || 0}
                                onChange={handleInstallationLocationChange}
                                options={locationOptions}
                                sx={{ minWidth: 200 }}
                            />
                        </Box>

                        <Box sx={styles.formRow}>
                            <SelectField
                                label="Solar Optimisers"
                                value={curArray.optimiser?.uid || 0}
                                onChange={handleOptimiserChange}
                                options={optimiserOptions}
                                sx={{ minWidth: 200 }}
                            />
                            {curArray.optimiser?.uid > 0 && (
                                <NumberInput
                                    label="Qty"
                                    value={curArray.qty_optimisers || 0}
                                    onChange={(val) => updateCurrentArray({ qty_optimisers: Math.min(val, curArray.numPanels || 0) })}
                                    decimalScale={0}
                                    disabled={curArray.optimiser?.type === 'SE'}
                                    sx={{ width: 80 }}
                                />
                            )}
                        </Box>

                        {/* Rail/clamp colour — Silver is the default (Black has long supply lead times). */}
                        <Box sx={styles.formRow}>
                            <FormControl component="fieldset" size="small">
                                <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>Rail Colour</FormLabel>
                                <RadioGroup
                                    row
                                    value={curArray.railColour || 'silver'}
                                    onChange={(e) => updateCurrentArray({ railColour: e.target.value })}
                                >
                                    <FormControlLabel value="silver" control={<Radio size="small" />} label="Silver" />
                                    <FormControlLabel value="black" control={<Radio size="small" />} label="Black" />
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        <Button
                            variant="contained"
                            sx={{ bgcolor: '#04c659', '&:hover': { bgcolor: '#04a659' }, alignSelf: 'flex-start' }}
                            onClick={handleCalculate}
                        >
                            Get Data
                        </Button>
                    </Box>

                    {/* Panel Layout Sets for Rails/Hooks Calculation */}
                    <PanelLayoutSets
                        layoutSets={curArray.layoutSets || []}
                        totalPanels={curArray.numPanels || 0}
                        panelDimensions={{
                            width_mm: curArray.width_mm || 1134,
                            height_mm: curArray.height_mm || 1722
                        }}
                        onChange={handleLayoutSetsChange}
                        arrayNumber={curArray.arrayNumber || currentArray + 1}
                    />

                    {/* Visual Controls */}
                    <Grid container spacing={4} sx={styles.visualControls}>
                        <Grid item>
                            <Typography sx={styles.controlHeader}>Roof Pitch</Typography>
                            <RoofPitch onChange={handleRoofPitchSlider} roofpitch={curArray.roofPitch || 35} />
                        </Grid>
                        <Grid item>
                            <Typography sx={styles.controlHeader}>Orientation</Typography>
                            <CompassControl onChange={handleCompassSlider} degrees={curArray.orientation || 0} />
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    )
}

export default PanelsTab
